import React from 'react';
import {
  Button,
  HStack,
  Icon,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { 
  FiDownload,
  FiFile, 
  FiFileText, 
  FiImage,
  FiVideo,
  FiMusic,
} from 'react-icons/fi';
import { assetUrl } from '../../utils/url';

export interface FilePreviewProps {
  url: string;
  name?: string;
  mimeType?: string;
  size?: number;
  showInline?: boolean;
  buttonOnly?: boolean;
}

const FilePreview: React.FC<FilePreviewProps> = ({
  url,
  name,
  mimeType = '',
  size,
  buttonOnly = false,
}) => {
  
  const fullUrl = assetUrl(url) || url;
  const fileName = name || url.split('/').pop() || 'file';
  const shortenName = (n: string, max = 34) => {
    const base = String(n || '').trim();
    if (base.length <= max) return base;
    const dot = base.lastIndexOf('.');
    const ext = dot > 0 ? base.slice(dot) : '';
    const keep = Math.max(10, max - (ext.length + 3));
    return `${base.slice(0, keep)}…${ext}`;
  };
  const displayName = shortenName(fileName);
  const mime = mimeType.toLowerCase();
  
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');
  const mutedText = useColorModeValue('gray.600', 'gray.300');

  // Determine file type and icon
  const getFileInfo = () => {
    if (mime.startsWith('image/')) {
      return { type: 'image', icon: FiImage, color: 'purple.500', canPreview: true };
    }
    if (mime === 'application/pdf') {
      return { type: 'pdf', icon: FiFileText, color: 'red.500', canPreview: true };
    }
    if (mime.startsWith('video/')) {
      return { type: 'video', icon: FiVideo, color: 'pink.500', canPreview: true };
    }
    if (mime.startsWith('audio/')) {
      return { type: 'audio', icon: FiMusic, color: 'green.500', canPreview: true };
    }
    if (mime.includes('word') || mime.includes('document')) {
      return { type: 'document', icon: FiFileText, color: 'blue.500', canPreview: false };
    }
    if (mime.includes('sheet') || mime.includes('excel')) {
      return { type: 'spreadsheet', icon: FiFile, color: 'green.600', canPreview: false };
    }
    if (mime.includes('presentation') || mime.includes('powerpoint')) {
      return { type: 'presentation', icon: FiFile, color: 'orange.500', canPreview: false };
    }
    return { type: 'other', icon: FiFile, color: 'gray.500', canPreview: false };
  };

  const fileInfo = getFileInfo();
  const sizeKB = typeof size === 'number' ? Math.round(size / 1024) : undefined;
  const sizeMB = sizeKB && sizeKB > 1024 ? (sizeKB / 1024).toFixed(1) : undefined;
  const sizeStr = sizeMB ? `${sizeMB} MB` : sizeKB ? `${sizeKB} kB` : '';

  // Action button handler
  const handleDownload = async () => {
    const fallback = () => {
      try {
        const a = document.createElement('a');
        a.href = fullUrl;
        a.setAttribute('download', fileName);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch {}
    };
    try {
      const res = await fetch(fullUrl);
      if (!res.ok) return fallback();
      const blob = await res.blob();
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.setAttribute('download', fileName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(urlObj), 2000);
    } catch {
      fallback();
    }
  };

  // Button-only compact variant (for tight sidebars like Přílohy)
  if (buttonOnly) {
    return (
      <Button
        size="xs"
        leftIcon={<FiDownload />}
        colorScheme="blue"
        variant="outline"
        onClick={handleDownload}
      >
        Stáhnout
      </Button>
    );
  }

  // Default: row with icon, truncated name and action
  return (
    <HStack
      justify="space-between"
      p={3}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      bg={cardBg}
      flexWrap="wrap"
      w="100%"
    >
      <HStack flex={1} minW={0}>
        <Icon as={fileInfo.icon} color={fileInfo.color} flexShrink={0} />
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <Text fontWeight="medium" isTruncated maxW="100%">
            {displayName}
          </Text>
          {sizeStr && <Text fontSize="xs" color={mutedText}>{sizeStr}</Text>}
        </VStack>
      </HStack>
      <HStack spacing={2} flexShrink={0}>
        <Button size="sm" leftIcon={<FiDownload />} colorScheme="blue" onClick={handleDownload}>
          Stáhnout
        </Button>
      </HStack>
    </HStack>
  );
};

export default FilePreview;
